import { ALL_DRESSCODES, ALL_SPECIAL_TYPES } from "../../constants/EventConstants";

// How a proposed value reads in the review: in the words the app shows, not as stored.

export const FIELD_LABELS = {
  title: "Titel",
  descriptionGerman: "Beschreibung (DE)",
  descriptionEnglish: "Beschreibung (EN)",
  musicGenre: "Genres",
  image: "Bild",
  instagramName: "Instagram",
  address: "Adresse",
  latitude: "Breitengrad",
  longitude: "Längengrad",
  entryPrice: "Vorverkauf",
  boxOfficePrice: "Abendkasse",
  boxOfficeAvailability: "Abendkasse – Verfügbarkeit",
  ticketUrl: "Ticket-Link",
  minAge: "Mindestalter",
  dressCode: "Dresscode",
  drinkPriceIndicator: "Getränkepreis",
  specialType: "Event-Art",
  artists: "Line-up",
  eventDate: "Datum",
  startTime: "Beginn",
  endTime: "Ende",
  hasAwarenessTeam: "Awareness-Team",
  admission: "Eintritt",
  weekdays: "Öffnungszeiten",
};

const BOX_OFFICE = {
  LIMITED: "Begrenzt",
  UNLIMITED: "Unbegrenzt",
  PRESALE_ONLY: "Keine Abendkasse",
  UNKNOWN: "Nicht bekannt",
};
const DRINK = { 1: "€ · unter 4,30 €", 2: "€€ · 4,30–5,00 €", 3: "€€€ · über 5,00 €" };

export const LIST_FIELDS = new Set(["musicGenre", "artists", "weekdays"]);
export const LONG_FIELDS = new Set(["descriptionGerman", "descriptionEnglish", "address", "title"]);
export const LINK_FIELDS = new Set(["ticketUrl", "image"]);

const labelOf = (list, value) => list.find((o) => o.value === value)?.label ?? value;

const price = (v) =>
  `${new Intl.NumberFormat("de-AT", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(v))} €`;

export const isEmpty = (v) => v == null || v === "" || (Array.isArray(v) && v.length === 0);

const num = (v) => (v == null || v === "" ? null : Number(v));

/** The entry as the app reads it: free entry is an open box office at 0 € without a paid presale. */
export function formatAdmission(value) {
  if (!value || typeof value !== "object") return "—";
  const presale = num(value.entryPrice);
  const box = num(value.boxOfficePrice);
  const status = value.boxOfficeAvailability;
  const open = status === "LIMITED" || status === "UNLIMITED";
  if (open && box === 0 && (presale == null || presale === 0)) return "Gratis (0 €)";
  const parts = [];
  if (presale != null) parts.push(presale === 0 ? "kein Vorverkauf" : `VVK ${price(presale)}`);
  if (open && box != null) parts.push(`AK ${price(box)}${status === "LIMITED" ? " (begrenzt)" : ""}`);
  else if (status === "PRESALE_ONLY") parts.push("keine Abendkasse");
  else parts.push("Abendkasse nicht bekannt");
  return parts.join(" · ");
}

/** A single value as text; lists are shown as chips by the page. */
export function formatValue(field, value) {
  if (isEmpty(value)) return "—";
  switch (field) {
    case "admission":
      return formatAdmission(value);
    case "startTime":
    case "endTime":
      return String(value).slice(0, 5);
    case "eventDate": {
      const d = new Date(`${value}T12:00:00`);
      return Number.isNaN(d.valueOf())
        ? String(value)
        : d.toLocaleDateString("de-AT", { weekday: "short", day: "2-digit", month: "2-digit", year: "numeric" });
    }
    case "entryPrice":
      return Number(value) === 0 ? "Kein Vorverkauf (0 €)" : price(value);
    case "boxOfficePrice":
      return price(value);
    case "boxOfficeAvailability":
      return BOX_OFFICE[value] ?? value;
    case "drinkPriceIndicator":
      return DRINK[value] ?? String(value);
    case "dressCode":
      return labelOf(ALL_DRESSCODES, value);
    case "specialType":
      return labelOf(ALL_SPECIAL_TYPES, value);
    case "hasAwarenessTeam":
      return value ? "Ja, vor Ort" : "Nein";
    case "minAge":
      return `${value}+`;
    case "instagramName":
      return `@${value}`;
    default:
      return String(value);
  }
}

const DAYS = { Monday: "Mo", Tuesday: "Di", Wednesday: "Mi", Thursday: "Do", Friday: "Fr", Saturday: "Sa", Sunday: "So" };

const itemKey = (field, item) => {
  if (field === "artists") return String(item?.name ?? "").toLowerCase();
  if (field === "weekdays") return String(item?.day ?? "");
  return String(item);
};
export const itemLabel = (field, item) => {
  if (field === "weekdays") {
    const hours = [item?.open, item?.close].filter(Boolean).map((t) => String(t).slice(0, 5)).join("–");
    return `${DAYS[item?.day] ?? item?.day}${hours ? ` ${hours}` : ""}`;
  }
  if (field !== "artists") return String(item);
  return item?.instagram ? `${item.name} · @${item.instagram}` : String(item?.name ?? "");
};

/** A list change as kept, removed and added entries, in the order of the new list. */
export function listDiff(field, oldList, newList) {
  const before = Array.isArray(oldList) ? oldList : [];
  const after = Array.isArray(newList) ? newList : [];
  const oldKeys = new Map(before.map((i) => [itemKey(field, i), i]));
  const newKeys = new Set(after.map((i) => itemKey(field, i)));
  return [
    ...after.map((item) => {
      const old = oldKeys.get(itemKey(field, item));
      if (!old) return { item, change: "added" };
      return { item, change: itemLabel(field, old) === itemLabel(field, item) ? "kept" : "changed" };
    }),
    ...before.filter((i) => !newKeys.has(itemKey(field, i))).map((item) => ({ item, change: "removed" })),
  ];
}

export const confidenceLabel = (c) => ({ HIGH: "Sicher", MEDIUM: "Wahrscheinlich", LOW: "Unsicher" }[c] ?? c);

export function hostOf(url) {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

/** Open proposals grouped by event, events by date. */
export function groupByEvent(proposals) {
  const groups = new Map();
  for (const p of proposals) {
    if (!groups.has(p.markerId)) {
      groups.set(p.markerId, { markerId: p.markerId, title: p.markerTitle, eventDate: p.eventDate, items: [] });
    }
    groups.get(p.markerId).items.push(p);
  }
  return [...groups.values()].sort((a, b) => String(a.eventDate).localeCompare(String(b.eventDate)) || a.markerId - b.markerId);
}
